import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import walletIcon from '../../../assets/icons/wallet.svg';
import historyIcon from '../../../assets/icons/history.svg';
import noteIcon from '../../../assets/icons/note.svg';
import chatIcon from '../../../assets/icons/chat.svg';
import gearIcon from '../../../assets/icons/gear.svg';
import heartIcon from '../../../assets/icons/heart.svg';
import templatesIcon from '../../../assets/icons/templates.svg';
import signoutIcon from '../../../assets/icons/signout.svg';
import checkIcon from '../../../assets/icons/check.svg';
import def_user_icon from '../../images/def_user_icon.svg';
import starIcon from '../../../assets/icons/star.svg';
import briefcaseIcon from '../../../assets/icons/briefcase.svg';
import orderIcon from '../../../assets/icons/order.svg';

import ProfileMenuTabItem from "../items/profile_meni_tab_item";
import { getUserInfo } from "../../../api/usercall";

import { t } from "i18next";
import { useUnreadCount } from "../../../contexts/UnreadCountContext";
import monitoringService from "../../../services/monitoringService";

function Menu({ setActiveTab }) {
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [activeTab, setActiveTabState] = useState("");
    const [isBusiness, setIsBusiness] = useState(false);
    const [isDealer, setIsDealer] = useState(false);
    const [dealerRole, setDealerRole] = useState(null);

    const navigate = useNavigate();
    const { totalUnreadCount } = useUnreadCount();

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const data = await getUserInfo();
                if (data && data.user) {
                    setUserInfo(data.user);
                    setIsBusiness(data.user.user_type === "business");
                    setIsDealer(data.user.user_type === "dealer");
                    setDealerRole(data.user.dealer_role || null);
                }
            } catch (err) {
                console.error("Failed to fetch user information", err);
            }
        };

        fetchUserInfo().then(() => {
            const currentHash = window.location.hash.replace("#", "");
            if (!isBusiness && !isDealer && (currentHash === "tariffs" || currentHash === "counterpartyCheck")) {
                navigate("/profile", { replace: true });
                setActiveTab(null);
                setActiveTabState("");
                return;
            }
            if (currentHash) {
                setActiveTab(currentHash);
                setActiveTabState(currentHash);
            }
        });
    }, [setActiveTab, isBusiness, navigate]);

    const handleLogout = async () => {
        await monitoringService.trackLogout().catch(err => {
            console.warn('Failed to track logout:', err);
        });
        localStorage.clear();
        setShowLogoutModal(false);
        navigate("/sign");
    };

    const handleTabClick = (tabId) => {
        if (tabId === "logout") {
            setShowLogoutModal(true);
        } else {
            setActiveTab(tabId);
            setActiveTabState(tabId);
            navigate(`/profile#${tabId}`, { replace: true });
        }
    };

    const dealerMenuItems = {
        director: [
            { tabId: "finance", tabName: t('finance'), iconPath: walletIcon },
            { tabId: "myOrders", tabName: t('myOrders') || 'Менің тапсырыстарым', iconPath: orderIcon },
            { tabId: "ads", tabName: t('announcements'), iconPath: noteIcon },
            { tabId: "messages", tabName: t('messages'), iconPath: chatIcon },
            { tabId: "dealerEmployees", tabName: t('employees'), iconPath: briefcaseIcon },
            { tabId: "settings", tabName: t('settings'), iconPath: gearIcon },
            { tabId: "favorites", tabName: t('favorites'), iconPath: heartIcon },
            { tabId: "contractTemplates", tabName: t('contractTemplates'), iconPath: templatesIcon },
            { tabId: "counterpartyCheck", tabName: t('counterpartyCheck'), iconPath: checkIcon },
            { tabId: "myReviews", tabName: t('feedbacks'), iconPath: starIcon },
        ],
        financier: [
            { tabId: "finance", tabName: t('finance'), iconPath: walletIcon },
            { tabId: "walletTopUp", tabName: t('replenishWallet'), iconPath: walletIcon },
            { tabId: "paymentHistory", tabName: t('paymentHistory'), iconPath: historyIcon },
            { tabId: "settings", tabName: t('settings'), iconPath: gearIcon },
            { tabId: "counterpartyCheck", tabName: t('counterpartyCheck'), iconPath: checkIcon },
        ],
        manager: [
            { tabId: "myOrders", tabName: t('myOrders') || 'Менің тапсырыстарым', iconPath: orderIcon },
            { tabId: "ads", tabName: t('announcements'), iconPath: noteIcon },
            { tabId: "messages", tabName: t('messages'), iconPath: chatIcon },
            { tabId: "favorites", tabName: t('favorites'), iconPath: heartIcon },
            { tabId: "contractTemplates", tabName: t('contractTemplates'), iconPath: templatesIcon },
            { tabId: "settings", tabName: t('settings'), iconPath: gearIcon },
            { tabId: "myReviews", tabName: t('feedbacks'), iconPath: starIcon },
        ],
    };

    const menuItems = [
        ...(isBusiness
            ? [{ tabId: "finance", tabName: t('finance'), iconPath: walletIcon }]
            : isDealer
                ? (dealerMenuItems[dealerRole] || dealerMenuItems.manager)
                : [
                    { tabId: "walletTopUp", tabName: t('replenishWallet'), iconPath: walletIcon },
                    { tabId: "paymentHistory", tabName: t('paymentHistory'), iconPath: historyIcon },
                ]
        ),
        ...(!isBusiness && !isDealer ? [
            { tabId: "ads", tabName: t('announcements'), iconPath: noteIcon },
            { tabId: "messages", tabName: t('messages'), iconPath: chatIcon, mobileHidden: true },
            { tabId: "settings", tabName: t('settings'), iconPath: gearIcon },
            { tabId: "favorites", tabName: t('favorites'), iconPath: heartIcon, mobileHidden: true },
            { tabId: "contractTemplates", tabName: t('contractTemplates'), iconPath: templatesIcon },
        ] : []),
        ...(isBusiness ? [
            { tabId: "ads", tabName: t('announcements'), iconPath: noteIcon },
            { tabId: "messages", tabName: t('messages'), iconPath: chatIcon },
            { tabId: "settings", tabName: t('settings'), iconPath: gearIcon },
            { tabId: "favorites", tabName: t('favorites'), iconPath: heartIcon },
            { tabId: "contractTemplates", tabName: t('contractTemplates'), iconPath: templatesIcon },
            { tabId: "counterpartyCheck", tabName: t('counterpartyCheck'), iconPath: checkIcon },
        ] : []),
        { tabId: "logout", tabName: t('logout'), iconPath: signoutIcon },
    ];

    return (
        <div className="w-full md:w-[260px] md:flex-shrink-0 md:sticky md:top-[90px] md:self-start">
            <div className="bg-white rounded-2xl p-5">

                {/* User card */}
                <div className="flex items-center gap-3 mb-4">
                    <img
                        src={userInfo?.profilePicture || def_user_icon}
                        alt="Profile"
                        className="w-14 h-14 rounded-full object-cover border-2 border-gray-100 flex-shrink-0"
                    />
                    <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">
                            {userInfo?.name || ""}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                            id: {userInfo?.id || ""}
                        </p>
                        {isDealer && dealerRole && (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">
                                {t(dealerRole)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Stats row */}
                {userInfo && (
                    <div className="flex gap-2 mb-4">
                        {/* Balance */}
                        <div className="flex-1 bg-[#147F26]/5 rounded-xl px-3 py-2">
                            <p className="text-[10px] text-gray-400 mb-0.5">{t('yourBalance')}</p>
                            <p className="text-sm font-bold text-[#147F26]">
                                {userInfo.balance} <span className="font-normal text-xs">{t('units')}</span>
                            </p>
                        </div>

                        {/* Rating */}
                        <button
                            onClick={() => handleTabClick("myReviews")}
                            className={`flex-1 rounded-xl px-3 py-2 text-left transition-colors
                                ${activeTab === "myReviews" ? "bg-[#147F26]/5" : "bg-gray-50 hover:bg-[#0f5f1a]/5"}`}
                        >
                            <div className="flex items-center gap-1 mb-0.5">
                                <img src={starIcon} alt="star" className="w-3 h-3" />
                                <p className="text-[10px] text-gray-400">{t('rating')}</p>
                            </div>
                            <p className="text-sm font-bold text-gray-700">
                                {String(userInfo.rating).substring(0, 3)}
                                <span className="text-xs font-normal text-gray-400 ml-1">({userInfo.reviews})</span>
                            </p>
                        </button>
                    </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-100 mb-2" />

                {/* Menu items */}
                <ul className="space-y-0.5 p-0 m-0 list-none">
                    {menuItems.map((item) => (
                        <div key={item.tabId} className={item.mobileHidden ? "hidden md:block" : ""}>
                            <ProfileMenuTabItem
                                setActiveTab={handleTabClick}
                                tabId={item.tabId}
                                tabName={item.tabName}
                                iconPath={item.iconPath}
                                iconComponent={item.iconComponent}
                                className={activeTab === item.tabId ? "menu-item active" : "menu-item"}
                                unreadCount={item.tabId === "messages" ? totalUnreadCount : 0}
                            />
                        </div>
                    ))}
                </ul>
            </div>

            {/* Logout modal */}
            {showLogoutModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-72 mx-4">
                        <h2 className="text-base font-semibold text-gray-900 text-center mb-5">
                            {t('logoutPrompt')}
                        </h2>
                        <div className="flex gap-3">
                            <button
                                onClick={handleLogout}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
                            >
                                {t('yes')}
                            </button>
                            <button
                                onClick={() => setShowLogoutModal(false)}
                                className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                            >
                                {t('no')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Menu;
